import React, {useEffect, useState} from 'react';
import './Login.scss';
import {Box, Typography} from '@mui/material';
import ArrTextField from '../../components/common/arr-text-field/ArrTextField';
import ArrButton from '../../components/common/arr-button/ArrButton';
import ArrBanner from '../../components/common/arr-banner/ArrBanner';
import {ReactComponent as ErrorIcon} from '../../../src/assets/images/error-icon.svg';
import {ReactComponent as LoginGradient} from '../../../src/assets/images/login-gradient.svg';
import {t} from 'i18next';
import BASE_URL, {SSO_ENABLED} from '../../config/env';
import {v4 as uuidv4} from 'uuid';

import {logoutOtherSession} from '../../api/endpoints/authenticationApi';
import ArrModal from '../../components/common/arr-modal/ArrModal';
import {GA_ERROR_CODES, GA_ERROR_TYPES} from '../../constants/analyticsConstants';

const Login: React.FC = () => {
  const SSO_Flag = Boolean(SSO_ENABLED === 'true' ? true : false);

  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isLoginError, setIsLoginError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showActiveModal, setShowActiveModal] = useState(false);

  const query = new URLSearchParams(window.location.search);
  useEffect(() => {
    const savedEmail = sessionStorage.getItem('login-email');
    if (savedEmail) {
      setEmail(savedEmail);
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem('login-email', email.trim());
  }, [email]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    if (value.length >= 6) {
      const newEmailRegex =
        /^[A-Za-z0-9_%+-]+(?:\.[A-Za-z0-9_%+-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;
      setIsEmailValid(newEmailRegex.test(value));
    } else {
      setIsEmailValid(true);
    }
  };

  const handleClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email || !isEmailValid) {
      setIsLoginError(true);
      return;
    }

    setLoading(true);

    let devSessionId = localStorage.getItem('dev-session-id');
    if (!devSessionId) {
      devSessionId = uuidv4();
      localStorage.setItem('dev-session-id', devSessionId);
    }

    const encodedEmail = encodeURIComponent(email.trim());
    const encodedDevSession = encodeURIComponent(devSessionId);

    const targetUrl =
      `${BASE_URL}/usermanagement/api/v1/login/${SSO_Flag ? 'ssologin' : 'normallogin'}` +
      `?login_hint=${encodedEmail}&dev_session_id=${encodedDevSession}`;

    window.location.assign(targetUrl);
  };

  useEffect(() => {
    const errorParamRaw = query.get('error');
    const errorParam = errorParamRaw?.toUpperCase();

    if (!errorParam) return;

    setIsLoginError(true);

    switch (errorParam) {
      case 'EMAIL_LOCKED':
        setErrorMessage(t('login.emailLockedError'));
        break;

      case 'ACCESS_DENIED':
      case 'INVALID_AUTH':
        setErrorMessage(t('login.accessDeniedError'));
        break;

      case 'INACTIVE_USER':
        setErrorMessage(t('login.userInActiveError'));
        break;

      case 'INVALID_IP':
        setErrorMessage(t('login.invalidIpError'));
        break;

      case '20001000018':
        setErrorMessage(t('login.noProjectAssignedError'));
        break;

      case 'DUPE_SESSION':
        setIsLoginError(false);
        setShowActiveModal(true);
        break;

      default:
        setErrorMessage(t('login.ssoError'));
    }
  }, [query]);

  const startNewSession = async () => {
    const sessionEmail = sessionStorage.getItem('login-email');
    const queryEmail = query.get('login-email');

    const savedEmail = sessionEmail || queryEmail;

    if (!savedEmail) return;

    try {
      setLoading(true);
      await logoutOtherSession(savedEmail);
      setShowActiveModal(false);
      window.history.replaceState({}, document.title, window.location.pathname);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="login-page">
      <Box className="login-container">
        <LoginGradient
          className="login-container-gradient"
          aria-hidden="true"
          focusable="false"
        />
        {/* Left side with branding */}

        <Box className="login-left">
          <Box className="branding">
            <img
              src="./aurora-review-logo.svg"
              alt={t('login.auroraLogoAlt')}
              data-testid={`login-img`}
            />
          </Box>
        </Box>
        {/* Right side with form */}
        <Box className="login-right">
          <Box className="login-box">
            <Typography
              id="login-title"
              data-testid="login-title"
              className="typo-text headline2_size login-title"
            >
              {t('login.signInMessage')}
            </Typography>

            <Typography
              id="login-subtitle"
              className="typo-text body2_size login-subtitle"
              data-testid={`login-enter-email-address`}
            >
              {t('login.enterEmailAddress')}
            </Typography>

            {isLoginError && (
              <Box className="login-error" role="alert" aria-live="assertive">
                <ArrBanner
                  dataTestId="sso-error"
                  type="contextual"
                  variant={
                    errorMessage == t('login.ssoError') ||
                    t('login.noProjectAssignedError') ||
                    errorMessage == t('login.accessDeniedError')
                      ? 'error'
                      : 'warning'
                  }
                  bannerContent={errorMessage}
                  gaTrackingInfo={{
                    code: GA_ERROR_CODES.NOT_AUTHORIZED_401,
                    type: GA_ERROR_TYPES.AUTHENTICATION_ERROR,
                  }}
                  startIcon={
                    <ErrorIcon
                      aria-hidden="true"
                      focusable="false"
                      className={
                        errorMessage == t('login.ssoError') ||
                        t('login.noProjectAssignedError') ||
                        errorMessage == t('login.accessDeniedError')
                          ? 'error'
                          : 'warning'
                      }
                    />
                  }
                />
              </Box>
            )}

            <form
              className="login-form"
              aria-labelledby="login-title"
              onSubmit={handleClick}
              noValidate
              data-testid={`login-continue`}
            >
              <Box className="form-group">
                <ArrTextField
                  state={isEmailValid ? 'enabled' : 'error'}
                  value={email}
                  label={t('login.emailAddress')}
                  supportText={
                    isEmailValid ? '' : t('login.emailValidationMessage')
                  }
                  showErrorIcon={!isEmailValid}
                  onChange={handleEmailChange}
                  id="email-input"
                  data-testid="email-input"
                  aria-invalid={!isEmailValid}
                  dataTestId="email-input"
                />
              </Box>

              <ArrButton
                dataTestId="login-continue-button"
                type="submit"
                variant="primary"
                title={t('common.continue')}
                disabled={!(email.length >= 6 && isEmailValid)}
                fullWidth
                loading={loading}
                aria-busy={loading}
              >
                {t('common.continue')}
              </ArrButton>
            </form>
          </Box>
        </Box>
      </Box>
      <ArrModal
        showModal={showActiveModal}
        title={t('login.activeSessionTitle')}
        description={t('login.activeSessionDesc')}
        onClose={() => {
          setShowActiveModal(false);
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }}
        autoFocusCloseButton={true}
        dataTestId="login-modal"
        buttons={[
          {
            id: 'cancel-button',
            label: t('common.cancel'),
            dataTestId: 'cancel-button',
            onClick: () => {
              setShowActiveModal(false);
              const cleanUrl = window.location.pathname;
              window.history.replaceState({}, document.title, cleanUrl);
            },
            variant: 'secondary',
          },
          {
            id: 'start-new-session-button',
            label: t('login.startNewSessionBtn'),
            dataTestId: 'start-new-session-button',
            onClick: startNewSession,
            variant: 'primary',
            loading: loading,
            disabled: loading,
          },
        ]}
      />
    </Box>
  );
};

export default Login;
